import React, { useContext, useState, useEffect } from "react";
import { Context } from "../../../../Context";
import { API } from "aws-amplify";
import { toast } from "react-toastify";

const EndDay = () => {
  const { institutionData, setLoader, refreshData } = useContext(Context);
  const type = institutionData?.bar_integration === "Yes" ? "bar" : "cafe";
  const [allOrders, setAllOrders] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (institutionData?.dateId) {
      const key = institutionData.dateId;
      const yy = '20' + key.slice(0, 2);
      const mm = parseInt(key.slice(2, 4), 10) - 1;
      const dd = parseInt(key.slice(4, 6), 10);
      const date = new Date(yy, mm, dd);
      return date.toISOString().slice(0, 10);
    }
    return new Date().toISOString().slice(0, 10);
  });
  const [leftSideData, setLeftSideData] = useState({});
  const [rightSideSections, setRightSideSections] = useState([]);
  const [reportSaved, setReportSaved] = useState(false);
  const [previousDayData, setPreviousDayData] = useState(null);
  const [previousDate, setPreviousDate] = useState(null);
  const [hasPendingOrders, setHasPendingOrders] = useState(false);
  const [showStockPreview, setShowStockPreview] = useState(false);

  // Utility functions for stock report
  const dateToKey = (isoDate) => {
    const [yyyy, mm, dd] = isoDate.split('-');
    const yy = yyyy.slice(-2);
    const m = mm.padStart(2, '0');
    const d = dd.padStart(2, '0');
    return `${yy}${m}${d}`;
  };

  const keyToDate = (key) => {
    if (!key || key.length !== 6) return new Date();
    const yy = '20' + key.slice(0, 2);
    const mm = parseInt(key.slice(2, 4), 10) - 1;
    const dd = parseInt(key.slice(4, 6), 10);
    return new Date(yy, mm, dd);
  };

  const dateIdToIso = (dateId) => {
    if (!dateId || dateId.length !== 6) return null;
    const yy = '20' + dateId.slice(0, 2);
    const mm = dateId.slice(2, 4).padStart(2, '0');
    const dd = dateId.slice(4, 6).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const safeNum = (val) => {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    const m = String(val).match(/-?[\d,.]+/);
    if (!m) return 0;
    return Number(String(m[0]).replace(/,/g, "")) || 0;
  };

  const parseVolumeToMl = (str) => {
    if (!str && str !== 0) return 0;
    if (typeof str === "number") return str;
    const s = String(str).trim().toLowerCase();
    if (s.includes("piece")) return Number(s.match(/([\d,.]+)/)?.[1] || 1);
    const nMatch = s.match(/([\d,.]+)/);
    if (!nMatch) return 0;
    let num = Number(nMatch[1].replace(/,/g, ""));
    if (s.includes("l") && !s.includes("ml")) {
      num = num * 1000;
    } else if (s.includes("ml")) {
      // num stays
    } else if (s.includes("kg")) {
      num = num * 1000;
    } else if (s.includes("g")) {
      // num stays
    }
    return Math.round(num);
  };

  const formatBottlesMl = (mlTotal, oneBottleMl) => {
    const bottleSize = oneBottleMl > 0 ? oneBottleMl : 1000;
    const bottles = Math.floor(mlTotal / bottleSize);
    const remainder = mlTotal - bottles * bottleSize;
    return { bottles, remainder };
  };

  useEffect(() => {
    if (institutionData?.institution) {
      initializeEndDayData();
    }
  }, [institutionData, selectedDate]);

  const fetchStockData = async () => {
    try {
      if (!institutionData?.institution) return [];
      const response = await API.get(
        "main",
        `/admin/stock-list-items/${institutionData.institution}`
      );
      return response || [];
    } catch (error) {
      console.error("Error fetching stock data:", error);
      return [];
    }
  };

  const fetchReportResponse = async () => {
    try {
      return await API.get(
        "main",
        `/admin/end-day-report/${institutionData.institution}?date=${selectedDate}&type=${type}`,
        {}
      );
    } catch (error) {
      console.error("Error fetching report:", error);
      return { success: false };
    }
  };

  const initializeEndDayData = async () => {
    try {
      setLoading(true);

      const [ordersData, stockData, existingResponse] = await Promise.all([
        fetchOrdersData(),
        fetchStockData(),
        fetchReportResponse(),
      ]);

      const previousData = existingResponse.previousReport;
      setPreviousDayData(previousData);
      setPreviousDate(previousData ? dateIdToIso(previousData.dateId) : null);

      calculateLeftSideData(ordersData, selectedDate, previousData);

      const dateKey = dateToKey(selectedDate);

      const pendingOrders = ordersData.filter((order) =>
        (!order.paymentStatus) && !order.cancel
      );
      setHasPendingOrders(pendingOrders.length > 0);

      const report = existingResponse.report;
      if (report && existingResponse.success && report.closingBalance !== undefined) {
        setRightSideSections(report.rightSideSections || []);
        setReportSaved(true);
        toast.info("Loaded existing report for this date");
      } else {
        loadRightSideSections();
        setReportSaved(false);
      }

      setStocks(stockData);
    } catch (error) {
      console.error("Error initializing end day data:", error);
      toast.error("Failed to load end day data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersData = async () => {
    const result = await API.get(
      "main",
      `/admin/list-orders/${institutionData.institution}?type=${type}`,
      {}
    );

    setAllOrders(result || []);
    return result || [];
  };

  const loadRightSideSections = () => {
    setRightSideSections([
      {
        heading: "PAYMENTS",
        entries: [
          { name: "CASH TO OWNER", value: 0 },
          { name: "OFFICE DEPOSIT", value: 0 },
          { name: "OTHER EXP.", value: 0 },
        ],
      },
    ]);
  };

  const calculateLeftSideData = (orders, date, previousData) => {
    const dateKey = dateToKey(date);

    const ordersForDate = orders.filter((order) =>
      String(order.orderId || '').startsWith(dateKey)
    );

    const paidOrdersForDate = ordersForDate.filter((order) => order.paymentStatus === true);

    let totalBilledAmount = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalServiceTax = 0;
    let totalBarAmount = 0;
    let totalKitchenAmount = 0;
    let totalBeverageAmount = 0;
    let totalPayments = {};

    paidOrdersForDate.forEach((order) => {
      const amount = Number(order.totalAmount) || 0;
      totalBilledAmount += amount;
      totalCgst += Number(order.cgst) || 0;
      totalSgst += Number(order.sgst) || 0;
      totalServiceTax += Number(order.serviceTax) || 0;

      const payMethod = (
        order.paymentMethod ||
        order.paymentMode ||
        "Cash"
      ).toLowerCase();
      totalPayments[payMethod] = (totalPayments[payMethod] || 0) + amount;

      (order.items || []).forEach((item) => {
        const itemAmount =
          (Number(item.price) || 0) * (Number(item.quantity) || 0);
        const itemType = String(item.type || "").toLowerCase();
        const itemCategory = String(item.category || "").toLowerCase();

        if (itemCategory === "beverage") {
          totalBeverageAmount += itemAmount;
        } else if (itemType === "bar") {
          totalBarAmount += itemAmount;
        } else if (itemType === "cafe" || itemType === "kitchen") {
          totalKitchenAmount += itemAmount;
        }
      });
    });

    const totalTax = totalCgst + totalSgst + totalServiceTax;
    const totalReceiptsAmount = totalBarAmount + totalKitchenAmount + totalBeverageAmount + totalTax;

    const openingBalance = previousData?.closingBalance || 0;
    const averageSales = calculateAverageSales(orders, date);

    setLeftSideData({
      openingBalance: openingBalance,
      averageSales: averageSales,
      kitchenSales: totalKitchenAmount,
      beverageSales: totalBeverageAmount,
      barSales: totalBarAmount,
      sgst: totalSgst,
      cgst: totalCgst,
      serviceTax: totalServiceTax,
      totalSales: totalBilledAmount,
      totalTax: totalTax,
      grandTotal: totalReceiptsAmount,
      paymentBreakdown: totalPayments,
      orderCount: paidOrdersForDate.length,
      date: date,
    });
  };

  const calculateAverageSales = (allOrders, currentDate) => {
    const thirtyDaysAgo = new Date(currentDate);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const currentDayStart = new Date(currentDate);
    currentDayStart.setHours(0, 0, 0, 0);

    const historicalOrders = allOrders.filter((order) => {
      const orderDate = new Date(Number(order.createdAt));
      return orderDate >= thirtyDaysAgo && orderDate < currentDayStart && order.paymentStatus === true;
    });

    if (historicalOrders.length === 0) return 0;

    const dailyTotals = {};
    historicalOrders.forEach((order) => {
      const dateKey = new Date(Number(order.createdAt)).toDateString();
      if (!dailyTotals[dateKey]) {
        dailyTotals[dateKey] = 0;
      }
      dailyTotals[dateKey] += Number(order.totalAmount) || 0;
    });

    const totalSales = Object.values(dailyTotals).reduce(
      (sum, daily) => sum + daily,
      0
    );
    const numberOfDays = Object.keys(dailyTotals).length;

    return numberOfDays > 0 ? totalSales / numberOfDays : 0;
  };

  const buildStockReportData = (isoDate, stocks, typeFilter = "bar") => {
    const key = dateToKey(isoDate);
    const saleSizes = new Set(["60 ml", "30 ml"]);
    const rateSizes = new Set();
    const groups = {};

    stocks
      .filter((s) => s.type === typeFilter)
      .forEach((stock) => {
        const update = (stock.stockupdate || {})[key] || {};
        Object.keys(update || {}).forEach((k) => {
          if (k.startsWith("unitRate_")) {
            rateSizes.add(k.replace("unitRate_", ""));
          } else if (
            ![
              "openingStock",
              "opening",
              "open",
              "received",
              "transferred",
            ].includes(k)
          ) {
            saleSizes.add(k);
          }
        });

        const cat = stock.category || "UNCATEGORISED";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push({ stock, update });
      });

    const saleCols = Array.from(saleSizes).sort((a, b) => {
      const na = Number(String(a).match(/\d+/)?.[0] || 0);
      const nb = Number(String(b).match(/\d+/)?.[0] || 0);
      return nb - na || String(a).localeCompare(b);
    });
    const rateCols = Array.from(rateSizes).sort((a, b) => {
      const na = Number(String(a).match(/\d+/)?.[0] || 0);
      const nb = Number(String(b).match(/\d+/)?.[0] || 0);
      return na - nb || String(a).localeCompare(b);
    });

    const categories = [];
    let grandTotalAmount = 0;

    // Sort categories with BEVERAGE last
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      // BEVERAGE category should come last
      if (a.toUpperCase() === "BEVERAGE" && b.toUpperCase() !== "BEVERAGE") return 1;
      if (b.toUpperCase() === "BEVERAGE" && a.toUpperCase() !== "BEVERAGE") return -1;
      // All other categories sort alphabetically
      return a.localeCompare(b);
    });

    sortedCategories.forEach((cat) => {
      const items = groups[cat].map(({ stock, update }) => {
        const hasUpdate = Object.keys(update).length > 0;
        const openingRaw = hasUpdate
          ? update.openingStock ?? update.opening ?? update.open ?? 0
          : stock.quantity ?? 0;
        const receivedRaw = update.received ?? 0;
        const transferredRaw = update.transferred ?? 0;

        const openingMl = parseVolumeToMl(openingRaw);
        const receivedMl = parseVolumeToMl(receivedRaw);
        const transferredMl = parseVolumeToMl(transferredRaw);
        const totalMl = openingMl + receivedMl;

        const sales = {};
        let salesVolumeMl = 0;
        saleCols.forEach((s) => {
          const cnt = safeNum(update[s] ?? 0);
          sales[s] = cnt;
          const num = Number(String(s).match(/\d+/)?.[0] || 0);
          if (num) salesVolumeMl += cnt * num;
        });

        let amount = 0;
        rateCols.forEach((r) => {
          const rate = safeNum(update[`unitRate_${r}`] ?? 0);
          const cnt = safeNum(update[r] ?? 0);
          amount += rate * cnt;
        });


        const isPiece = (stock.unit === 'piece' || (stock.quantity && String(stock.quantity).toLowerCase().includes('piece')));
        const closingMl = Math.max(0, totalMl - salesVolumeMl - transferredMl);
        const oneBottleMl = parseVolumeToMl(stock.oneBottleVolume || "1000 ml");

        let openingBTL, receivedBTL, totalBTL, transferredBTL, closingBTL;
        let openingPC = 0, receivedPC = 0, totalPC = 0, transferredPC = 0, closingPC = 0;

        if (isPiece) {
          openingBTL = { bottles: 0, remainder: 0 };
          receivedBTL = { bottles: 0, remainder: 0 };
          totalBTL = { bottles: 0, remainder: 0 };
          transferredBTL = { bottles: 0, remainder: 0 };
          closingBTL = { bottles: 0, remainder: 0 };

          openingPC = openingMl;
          receivedPC = receivedMl;
          totalPC = totalMl;
          transferredPC = transferredMl;
          closingPC = closingMl;
        } else {
          openingBTL = formatBottlesMl(openingMl, oneBottleMl);
          receivedBTL = formatBottlesMl(receivedMl, oneBottleMl);
          totalBTL = formatBottlesMl(totalMl, oneBottleMl);
          transferredBTL = formatBottlesMl(transferredMl, oneBottleMl);
          closingBTL = formatBottlesMl(closingMl, oneBottleMl);
        }

        return {
          stockId: stock.stockId,
          stockName: stock.stockName || stock.name || "-",
          openingMl,
          receivedMl,
          totalMl,
          transferredMl,
          openingBTL,
          receivedBTL,
          totalBTL,
          transferredBTL,
          sales,
          unitRates: rateCols.reduce((acc, r) => {
            acc[r] = safeNum(update[`unitRate_${r}`] ?? 0);
            return acc;
          }, {}),
          amount,
          closingMl,
          closingBTL,
          openingPC,
          receivedPC,
          totalPC,
          transferredPC,
          closingPC,
          oneBottleMl,
          oneBottleLabel: stock.oneBottleVolume || `${oneBottleMl} ml`,
          isPiece
        };
      });

      const catTotals = {
        openingMl: 0,
        receivedMl: 0,
        totalMl: 0,
        transferredMl: 0,
        sales: {},
        amount: 0,
        closingMl: 0,
        closingBottles: 0,
        closingRemainderMl: 0,
        openingPC: 0,
        receivedPC: 0,
        totalPC: 0,
        transferredPC: 0,
        closingPC: 0
      };
      saleCols.forEach((s) => (catTotals.sales[s] = 0));

      items.forEach((it) => {
        catTotals.openingMl += safeNum(it.openingMl);
        catTotals.receivedMl += safeNum(it.receivedMl);
        catTotals.totalMl += safeNum(it.totalMl);
        catTotals.transferredMl += safeNum(it.transferredMl);
        saleCols.forEach((s) => {
          catTotals.sales[s] += safeNum(it.sales[s]);
        });
        catTotals.amount += safeNum(it.amount);
        catTotals.closingMl += safeNum(it.closingMl);
        catTotals.closingBottles += safeNum(it.closingBTL.bottles);
        catTotals.closingRemainderMl += safeNum(it.closingBTL.remainder);
        catTotals.openingPC += safeNum(it.openingPC);
        catTotals.receivedPC += safeNum(it.receivedPC);
        catTotals.totalPC += safeNum(it.totalPC);
        catTotals.transferredPC += safeNum(it.transferredPC);
        catTotals.closingPC += safeNum(it.closingPC);
      });

      const oneBottleMl = items.length > 0 ? items[0].oneBottleMl : 1000;
      catTotals.openingBTL = formatBottlesMl(catTotals.openingMl, oneBottleMl);
      catTotals.receivedBTL = formatBottlesMl(catTotals.receivedMl, oneBottleMl);
      catTotals.totalBTL = formatBottlesMl(catTotals.totalMl, oneBottleMl);
      catTotals.transferredBTL = formatBottlesMl(catTotals.transferredMl, oneBottleMl);
      catTotals.closingBTL = {
        bottles: catTotals.closingBottles,
        remainder: catTotals.closingRemainderMl,
      };

      grandTotalAmount += catTotals.amount;
      categories.push({ category: cat, items, totals: catTotals });
    });

    return { categories, saleCols, rateCols, grandTotalAmount };
  };

  // Section management functions
  const addNewSection = () => {
    if (reportSaved) return; // Prevent edits after save
    const newSection = {
      heading: "NEW SECTION",
      entries: [{ name: "", value: 0 }],
    };
    setRightSideSections([...rightSideSections, newSection]);
  };

  const updateSectionHeading = (sectionIndex, newHeading) => {
    if (reportSaved) return; // Prevent edits after save
    const updatedSections = [...rightSideSections];
    updatedSections[sectionIndex].heading = newHeading;
    setRightSideSections(updatedSections);
  };

  const removeSection = (sectionIndex) => {
    if (reportSaved) return; // Prevent edits after save
    if (rightSideSections.length > 1) {
      setRightSideSections(
        rightSideSections.filter((_, index) => index !== sectionIndex)
      );
    }
  };

  const addEntryToSection = (sectionIndex) => {
    if (reportSaved) return; // Prevent edits after save
    const updatedSections = [...rightSideSections];
    updatedSections[sectionIndex].entries.push({ name: "", value: 0 });
    setRightSideSections(updatedSections);
  };

  const updateEntry = (sectionIndex, entryIndex, field, value) => {
    if (reportSaved) return; // Prevent edits after save
    const updatedSections = [...rightSideSections];
    if (field === "value") {
      updatedSections[sectionIndex].entries[entryIndex][field] =
        Number(value) || 0;
    } else {
      updatedSections[sectionIndex].entries[entryIndex][field] = value;
    }
    setRightSideSections(updatedSections);
  };

  const removeEntry = (sectionIndex, entryIndex) => {
    if (reportSaved) return; // Prevent edits after save
    const updatedSections = [...rightSideSections];
    if (updatedSections[sectionIndex].entries.length > 1) {
      updatedSections[sectionIndex].entries = updatedSections[
        sectionIndex
      ].entries.filter((_, index) => index !== entryIndex);
      setRightSideSections(updatedSections);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setReportSaved(false);
  };

  const handleSaveReport = async () => {
    try {
      if (hasPendingOrders) {
        toast.error("Cannot save report: There are pending unpaid non-cancelled orders. Please settle them first.");
        return;
      }

      // Check for unclosed dates with sales in chronological order
      if (allOrders && allOrders.length > 0) {
        const activeOrderDates = new Set();
        allOrders.forEach(order => {
          if (order.paymentStatus === true && !order.cancel && order.orderId) {
            const idStr = String(order.orderId);
            if (idStr.length >= 6) {
              const yy = '20' + idStr.slice(0, 2);
              const mm = idStr.slice(2, 4);
              const dd = idStr.slice(4, 6);
              const orderDateStr = `${yy}-${mm}-${dd}`;
              activeOrderDates.add(orderDateStr);
            }
          }
        });

        const skippedDates = Array.from(activeOrderDates).filter(dateStr => {
          if (previousDate) {
            return dateStr > previousDate && dateStr < selectedDate;
          } else {
            return dateStr < selectedDate;
          }
        });

        if (skippedDates.length > 0) {
          skippedDates.sort();
          const formattedSkipped = skippedDates[0].split('-').reverse().join('/');
          toast.error(`Cannot save report: The previous business day (${formattedSkipped}) has sales but has not been closed yet. Please close days in chronological order.`);
          return;
        }
      }

      if (!previousDayData) {
        const confirmed = window.confirm('This appears to be the first day close. Do you want to proceed with saving the report?');
        if (!confirmed) {
          return;
        }
      }

      setLoader(true);

      const rightSideTotal = rightSideSections.reduce((sectionSum, section) => {
        const sectionTotal = section.entries.reduce(
          (entrySum, entry) => entrySum + (Number(entry.value) || 0),
          0
        );
        return sectionSum + sectionTotal;
      }, 0);

      const closingBalance = ((leftSideData.grandTotal || 0) + (leftSideData.openingBalance || 0)) - rightSideTotal;

      const reportData = {
        date: selectedDate,
        dayStart: !previousDayData,
        type,
        leftSideData: { ...leftSideData, date: selectedDate },
        rightSideSections: rightSideSections.filter((section) =>
          section.entries.some((entry) => entry.name.trim() !== "")
        ),
        closingBalance,
        savedAt: new Date().toISOString(),
        savedBy: institutionData.userData?.userName || "Admin",
        index: institutionData.index
      };

      await API.post(
        "main",
        `/admin/end-day-report/${institutionData.institution}`,
        {
          body: reportData,
        }
      );

      setReportSaved(true);
      toast.success("End day report saved successfully!");

      // Refresh institution data to get updated dayStart status
      await refreshData();

      return true;
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(
        "Failed to save report: " + (error.message || "Unknown error")
      );
      return false;
    } finally {
      setLoader(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportSaved) {
      const saved = await handleSaveReport();
      if (!saved) {
        toast.warning("Unable to save report. Cannot download.");
        return;
      }
    }

    const reportContent = generateReportHTML();
    const printWindow = window.open("", "_blank");
    printWindow.document.write(reportContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateReportHTML = () => {
    const rightSideTotal = rightSideSections.reduce((sectionSum, section) => {
      const sectionTotal = section.entries.reduce(
        (entrySum, entry) => entrySum + (Number(entry.value) || 0),
        0
      );
      return sectionSum + sectionTotal;
    }, 0);
    const finalClosingBalance = ((leftSideData.grandTotal || 0) + (leftSideData.openingBalance || 0)) - rightSideTotal;

    const stockReportData = buildStockReportData(selectedDate, stocks);

    const currentTime = new Date().toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour12: true,
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    const formattedDate = selectedDate.split('-').reverse().join('/');
    const formattedPrevDate = previousDate ? previousDate.split('-').reverse().join('/') : 'N/A';

    return `
      <html>
        <head>
          <title>End Day Report - ${leftSideData.date}</title>
          <style>
            @page {
              margin: 8mm;
              size: A4;
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Times New Roman', serif;
              font-size: 10px;
              line-height: 1.1;
              color: #000;
              background: white;
              margin: 0;
              padding: 0;
            }
            
            .report-container {
              max-width: 200mm;
              margin: 0 auto;
              padding: 5mm;
            }
            
            .header-section {
              text-align: center;
              margin-bottom: 8mm;
              padding-bottom: 3mm;
              border-bottom: 1px solid #000;
            }
            
            .company-name {
              font-size: 14px;
              font-weight: 900;
              margin-bottom: 1mm;
              letter-spacing: 0.5px;
            }
            
            .report-title {
              font-size: 11px;
              font-weight: 900;
              margin: 1mm 0;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .date-section {
              font-size: 9px;
              margin: 1mm 0;
              color: #333;
              font-weight: 600;
            }
            
            .main-content {
              display: flex;
              justify-content: space-between;
              margin: 2mm 0;
              gap: 5mm;
            }
            
            .content-column {
              flex: 1;
            }
            
            .section-header {
              font-size: 11px;
              font-weight: 900;
              margin: 0 0 2mm 0;
              padding-bottom: 0.5mm;
              border-bottom: 1px solid #000;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .amount-row {
              display: flex;
              justify-content: space-between;
              margin: 0.5mm 0;
              padding: 0.5mm 0;
            }
            
            .amount-label {
              font-size: 10px;
              text-align: left;
              flex: 2;
              font-weight: 600;
            }
            
            .amount-value {
              font-size: 10px;
              text-align: right;
              flex: 1;
              font-family: 'Courier New', monospace;
              font-weight: 600;
            }
            
            .total-row {
              border-top: 1.5px solid #000;
              margin-top: 1mm;
              padding-top: 1mm;
              font-weight: 900;
              font-size: 11px;
            }
            
            .grand-total-row {
              border-top: 2px solid #000;
              margin-top: 3mm;
              padding-top: 1.5mm;
              font-weight: 900;
              font-size: 12px;
            }
            
            .closing-balance-section {
              margin-top: 5mm;
              padding-top: 3mm;
              border-top: 1.5px solid #000;
              text-align: center;
            }
            
            .closing-balance {
              font-size: 13px;
              font-weight: 900;
              letter-spacing: 0.5px;
            }
            
            .page-break {
              page-break-before: always;
            }
            
            .stock-report-header {
              text-align: center;
              margin-bottom: 5mm;
              padding-bottom: 2mm;
              border-bottom: 1px solid #000;
            }
            
            .stock-table {
              width: 100%;
              border-collapse: collapse;
              margin: 2mm 0;
              font-size: 8px;
            }
            
            .stock-table th,
            .stock-table td {
              border: 0.5px solid #000;
              padding: 1mm 0.3mm;
              text-align: center;
              vertical-align: middle;
              font-weight: 600;
            }
            
            .stock-table th {
              background-color: #f8f8f8;
              font-weight: 900;
              font-size: 7px;
              text-transform: uppercase;
              letter-spacing: 0.2px;
            }
            
            .stock-table .item-name {
              text-align: left;
              font-size: 8.5px;
              padding-left: 1.5mm;
              background-color: #fafafa;
              font-weight: 700;
              min-width: 18mm;
              max-width: 25mm;
              word-wrap: break-word;
              white-space: normal;
            }
            
            .stock-table .amount-column {
              text-align: right;
              padding-right: 1mm;
              font-family: 'Courier New', monospace;
              font-weight: 600;
            }
            
            .category-header {
              background-color: #e0e0e0 !important;
              font-weight: 900;
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .category-total {
              background-color: #d0d0d0 !important;
              font-weight: 900;
              font-size: 9px;
            }
            
            .grand-total-row-stock {
              background-color: #c8c8c8 !important;
              font-weight: 900;
              font-size: 10px;
              border-top: 1.5px solid #000;
              border-bottom: 1.5px solid #000;
            }
            
            .report-footer {
              margin-top: 5mm;
              padding-top: 3mm;
              border-top: 0.5px solid #ccc;
              text-align: center;
              font-size: 8px;
              color: #666;
              font-weight: 600;
            }
            
            .signature-section {
              margin-top: 5mm;
              display: flex;
              justify-content: space-between;
              font-size: 9px;
              font-weight: 600;
            }
            
            .signature-box {
              flex: 1;
              border-top: 0.5px solid #000;
              padding-top: 1.5mm;
              text-align: center;
              margin: 0 2mm;
              font-weight: 600;
            }
            
            .previous-day-section {
              margin-bottom: 3mm;
              padding: 2mm;
              background-color: #f9f9f9;
              border-left: 3px solid #ccc;
              font-size: 9px;
              font-weight: 600;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              
              .report-container {
                padding: 3mm;
              }
              
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <!-- First Page: Cashiering Report -->
            <div class="header-section">
              <div class="company-name">${institutionData?.companyName || "AMRAPALI TESTING"
      }</div>
              <div class="report-title">CASHIERING REPORT - ${institutionData?.companyName || "AMRAPALI TESTING"
      } BAR</div>
              <div class="date-section">
                Date: ${formattedDate} | Previous Date: ${formattedPrevDate} | Printed: ${currentTime}
              </div>
            </div>
            
            ${previousDayData ? `
              <div class="previous-day-section">
                <strong>Previous Day Summary (${formattedPrevDate}):</strong><br>
                Closing Balance: ₹${(previousDayData.closingBalance || 0).toFixed(2)} | 
                Total Sales: ₹${(previousDayData.leftSideData?.totalSales || 0).toFixed(2)} | 
                Orders: ${previousDayData.leftSideData?.orderCount || 0}
              </div>
            ` : ''}
            
            <div class="main-content">
              <div class="content-column">
                <div class="section-header">RECEIPTS</div>
                <div class="amount-row">
                  <div class="amount-label">Opening Balance (from ${formattedPrevDate})</div>
                  <div class="amount-value">₹${leftSideData.openingBalance?.toFixed(2) || "0.00"}</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">BAR SALES</div>
                  <div class="amount-value">₹${leftSideData.barSales?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">KITCHEN SALES</div>
                  <div class="amount-value">₹${leftSideData.kitchenSales?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">BEVERAGE SALES</div>
                  <div class="amount-value">₹${leftSideData.beverageSales?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row total-row">
                  <div class="amount-label">SUB TOTAL</div>
                  <div class="amount-value">₹${(leftSideData.barSales + leftSideData.kitchenSales + leftSideData.beverageSales)?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">SGST @ 2.5%</div>
                  <div class="amount-value">₹${leftSideData.sgst?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row">
                  <div class="amount-label">CGST @ 2.5%</div>
                  <div class="amount-value">₹${leftSideData.cgst?.toFixed(2) || "0.00"
      }</div>
                </div>
                <div class="amount-row total-row">
                  <div class="amount-label">TOTAL RECEIPTS</div>
                  <div class="amount-value">₹${leftSideData.grandTotal?.toFixed(2) || "0.00"
      }</div>
                </div>
                
                <div style="margin-top: 3mm;">
                  <div class="section-header">PAYMENT METHODS</div>
                  ${Object.entries(leftSideData.paymentBreakdown || {})
        .map(([method, amount]) => `
                    <div class="amount-row">
                      <div class="amount-label">${method.toUpperCase()}</div>
                      <div class="amount-value">₹${Number(amount).toFixed(2)}</div>
                    </div>
                  `)
        .join("")}
                  <div class="amount-row total-row">
                    <div class="amount-label">TOTAL</div>
                    <div class="amount-value">₹${leftSideData.totalSales?.toFixed(2) || "0.00"
      }</div>
                  </div>
                </div>
              </div>
              
              <div class="content-column">
                <div class="section-header">EXPENDITURES</div>
                ${rightSideSections
        .map((section, sectionIndex) => `
                  <div style="margin-bottom: 2mm;">
                    <div style="font-weight: bold; font-size: 10px; margin-bottom: 1mm; padding-bottom: 0.3mm; border-bottom: 0.5px solid #ccc;">
                      ${section.heading}
                    </div>
                    ${section.entries
            .filter((entry) => entry.name.trim() !== "")
            .map((entry) => `
                      <div class="amount-row">
                        <div class="amount-label">${entry.name}</div>
                        <div class="amount-value">₹${Number(entry.value).toFixed(2)}</div>
                      </div>
                    `)
            .join("")}
                    <div class="amount-row total-row">
                      <div class="amount-label">${section.heading} TOTAL</div>
                      <div class="amount-value">₹${section.entries
            .reduce((sum, entry) => sum + (Number(entry.value) || 0), 0)
            .toFixed(2)}</div>
                    </div>
                  </div>
                `)
        .join("")}
                
                <div class="grand-total-row">
                  <div class="amount-label">GRAND TOTAL EXPENDITURE</div>
                  <div class="amount-value">₹${rightSideTotal.toFixed(2)}</div>
                </div>
              </div>
            </div>
            
            <div class="closing-balance-section">
              <div class="closing-balance">CLOSING BALANCE: ₹${finalClosingBalance.toFixed(2)}</div>
              <div style="margin-top: 1mm; font-size: 9px; color: #666;">
                Orders: ${leftSideData.orderCount || 0} | Avg Sales: ₹${(leftSideData.averageSales || 0).toFixed(0)}
              </div>
            </div>
            
            <div class="signature-section">
              <div class="signature-box">
                <div style="margin-bottom: 1mm;">Prepared By</div>
                <div>${institutionData.userData?.userName || "Manager"}</div>
              </div>
              <div class="signature-box">
                <div style="margin-bottom: 1mm;">Verified By</div>
                <div>________________</div>
              </div>
              <div class="signature-box">
                <div style="margin-bottom: 1mm;">Accounts</div>
                <div>________________</div>
              </div>
            </div>
          </div>

          <!-- Page Break for Second Page -->
          <div class="page-break"></div>
          
          <div class="report-container">
            <!-- Second Page: Stock Report -->
            <div class="stock-report-header">
              <div class="company-name">${institutionData?.companyName || "AMRAPALI TESTING"
      }</div>
              <div class="report-title">DAILY STOCK REPORT - BAR INVENTORY</div>
              <div class="date-section">
                Date: ${formattedDate} | Printed: ${currentTime}
              </div>
            </div>

            <table class="stock-table">
              <thead>
                <tr>
                  <th rowspan="2" class="item-name">ITEM</th>
                  <th colspan="3">OPENING</th>
                  <th colspan="3">RECEIVED</th>
                  <th colspan="3">TOTAL</th>
                  <th colspan="3">TRANSFER</th>
                  <th colspan="4">SALES</th>
                  <th colspan="3">RATE</th>
                  <th colspan="3">CLOSING</th>
                  <th rowspan="2" class="amount-column">AMOUNT<br>(₹)</th>
                </tr>
                <tr>
                  <th>BTL</th>
                  <th>ML</th>
                  <th>PC</th>
                  <th>BTL</th>
                  <th>ML</th>
                  <th>PC</th>
                  <th>BTL</th>
                  <th>ML</th>
                  <th>PC</th>
                  <th>BTL</th>
                  <th>ML</th>
                  <th>PC</th>
                  <th>60ml</th>
                  <th>30ml</th>
                  <th>BTL</th>
                  <th>PC</th>
                  <th>60ml</th>
                  <th>30ml</th>
                  <th>BTL</th>
                  <th>BTL</th>
                  <th>ML</th>
                  <th>PC</th>
                </tr>
              </thead>
              <tbody>
                ${stockReportData.categories
        .map((cat) => {
          let catHtml = `
                      <tr>
                        <td colspan="23" class="category-header">${cat.category.toUpperCase()}</td>
                      </tr>
                    `;

          cat.items.forEach((item) => {
            const totalBottleSold = Object.keys(item.sales || {})
              .filter((k) => !["30 ml", "60 ml"].includes(k.toLowerCase()))
              .reduce((sum, k) => sum + safeNum(item.sales[k]), 0);
            const rate60ml = safeNum(item.unitRates?.["60 ml"] || 0);
            const rate30ml = safeNum(item.unitRates?.["30 ml"] || 0);
            const rateBottle = Object.keys(item.unitRates || {})
              .filter((k) => !["30 ml", "60 ml"].includes(k.toLowerCase()))
              .reduce((rate, k) => rate || safeNum(item.unitRates[k]), 0);

            catHtml += `
                        <tr>
                          <td class="item-name">${item.stockName || ""}</td>
                          <td>${item.openingBTL.bottles}</td>
                          <td>${item.isPiece ? "-" : Math.round(item.openingBTL.remainder)}</td>
                          <td>${item.openingPC || "-"}</td>
                          <td>${item.receivedBTL.bottles}</td>
                          <td>${item.isPiece ? "-" : Math.round(item.receivedBTL.remainder)}</td>
                          <td>${item.receivedPC || "-"}</td>
                          <td>${item.totalBTL.bottles}</td>
                          <td>${item.isPiece ? "-" : Math.round(item.totalBTL.remainder)}</td>
                          <td>${item.totalPC || "-"}</td>
                          <td>${item.transferredBTL.bottles}</td>
                          <td>${item.isPiece ? "-" : Math.round(item.transferredBTL.remainder)}</td>
                          <td>${item.transferredPC || "-"}</td>
                          <td>${safeNum(item.sales["60 ml"] || 0)}</td>
                          <td>${safeNum(item.sales["30 ml"] || 0)}</td>
                          <td>${item.isPiece ? "-" : totalBottleSold}</td>
                          <td>${item.isPiece ? totalBottleSold : "-"}</td>
                          <td>${rate60ml > 0 ? rate60ml.toFixed(0) : "-"}</td>
                          <td>${rate30ml > 0 ? rate30ml.toFixed(0) : "-"}</td>
                          <td>${rateBottle > 0 ? rateBottle.toFixed(0) : "-"}</td>
                          <td>${item.closingBTL.bottles}</td>
                          <td>${item.isPiece ? "-" : Math.round(item.closingBTL.remainder)}</td>
                          <td>${item.closingPC || "-"}</td>
                          <td class="amount-column">${safeNum(item.amount).toFixed(0)}</td>
                        </tr>
                      `;
          });

          const catTotalBottleSold = Object.entries(cat.totals.sales || {})
            .filter(([k]) => !["30 ml", "60 ml"].includes(k.toLowerCase()))
            .reduce((sum, [k, v]) => sum + safeNum(v), 0);

          catHtml += `
                      <tr class="category-total">
                        <td class="item-name">TOTAL ${cat.category.toUpperCase()}</td>
                        <td>${cat.totals.openingBTL.bottles}</td>
                        <td>${Math.round(cat.totals.openingBTL.remainder)}</td>
                        <td>${cat.totals.openingPC || "-"}</td>
                        <td>${cat.totals.receivedBTL.bottles}</td>
                        <td>${Math.round(cat.totals.receivedBTL.remainder)}</td>
                        <td>${cat.totals.receivedPC || "-"}</td>
                        <td>${cat.totals.totalBTL.bottles}</td>
                        <td>${Math.round(cat.totals.totalBTL.remainder)}</td>
                        <td>${cat.totals.totalPC || "-"}</td>
                        <td>${cat.totals.transferredBTL.bottles}</td>
                        <td>${Math.round(cat.totals.transferredBTL.remainder)}</td>
                        <td>${cat.totals.transferredPC || "-"}</td>
                        <td>${safeNum(cat.totals.sales["60 ml"] || 0)}</td>
                        <td>${safeNum(cat.totals.sales["30 ml"] || 0)}</td>
                        <td>${catTotalBottleSold}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>${cat.totals.closingBTL.bottles}</td>
                        <td>${Math.round(cat.totals.closingBTL.remainder)}</td>
                        <td>${cat.totals.closingPC || "-"}</td>
                        <td class="amount-column">${safeNum(cat.totals.amount).toFixed(0)}</td>
                      </tr>
                    `;

          return catHtml;
        })
        .join("")}

                <tr class="grand-total-row-stock">
                  <td class="item-name">GRAND TOTAL</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td class="amount-column">${stockReportData.grandTotalAmount.toFixed(0)}</td>
                </tr>
              </tbody>
            </table>

            <div class="report-footer">
              <div style="margin-bottom: 1mm; font-weight: bold; font-size: 9px;">
                TOTAL STOCK VALUE: ₹${stockReportData.grandTotalAmount.toFixed(2)}
              </div>
              <div style="font-size: 7px;">
                Generated: ${new Date().toLocaleDateString("en-IN")} | Page 2 of 2
              </div>
            </div>

            <div class="signature-section">
              <div class="signature-box">
                <div style="margin-bottom: 1mm;">Stock Verified</div>
                <div>________________</div>
              </div>
              <div class="signature-box">
                <div style="margin-bottom: 1mm;">Bar Manager</div>
                <div>________________</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const rightSideTotal = rightSideSections.reduce((sectionSum, section) => {
    const sectionTotal = section.entries.reduce(
      (entrySum, entry) => entrySum + (Number(entry.value) || 0),
      0
    );
    return sectionSum + sectionTotal;
  }, 0);
  const finalClosingBalance = ((leftSideData.grandTotal || 0) + (leftSideData.openingBalance || 0)) - rightSideTotal;

  const isEditable = !reportSaved;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            End Day Report
          </h1>

          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <label className="block text-sm font-medium text-gray-700">
                Select Date:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="bg-blue-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Orders Today:</span>{" "}
                {leftSideData.orderCount || 0}
              </div>
              <div className="bg-green-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Total Sales:</span> ₹
                {(leftSideData.totalSales || 0).toFixed(2)}
              </div>
              <div className="bg-orange-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Kitchen Sales:</span> ₹
                {(leftSideData.kitchenSales || 0).toFixed(2)}
              </div>
              <div className="bg-purple-50 px-3 py-2 rounded-lg">
                <span className="font-medium">Beverage Sales:</span> ₹
                {(leftSideData.beverageSales || 0).toFixed(2)}
              </div>
              {hasPendingOrders && (
                <div className="bg-red-50 text-red-800 px-3 py-2 rounded-lg">
                  ⚠ Pending Orders: Resolve before saving
                </div>
              )}
              {reportSaved && (
                <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg">
                  ✓ Report Saved
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="bg-white rounded-lg shadow-md p-6 mb-6"
          style={{ fontFamily: '"Times New Roman", serif' }}>
          <div className="text-center mb-6 border-b pb-4">
            <h2 className="text-xl font-bold">
              {institutionData?.companyName || "AMRAPALI TESTING"}
            </h2>
            <h3 className="text-lg font-semibold">
              CASHIERING REPORT{" "}
              {institutionData?.companyName || "AMRAPALI TESTING"} BAR
            </h3>
            <p className="text-sm text-gray-600">
              Print On: {leftSideData.date}{" "}
              {new Date().toLocaleTimeString("en-IN")}
            </p>
          </div>

          {previousDayData && (
            <div className="mb-6 p-4 bg-gray-100 border-l-4 border-gray-400 rounded">
              <h4 className="font-bold text-sm mb-2">Previous Day Summary ({previousDate})</h4>
              <div className="grid grid-cols-3 gap-4 text-xs text-gray-700">
                <div><strong>Closing Balance:</strong> ₹{(previousDayData.closingBalance || 0).toFixed(2)}</div>
                <div><strong>Total Sales:</strong> ₹{(previousDayData.leftSideData?.totalSales || 0).toFixed(2)}</div>
                <div><strong>Orders:</strong> {previousDayData.leftSideData?.orderCount || 0}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h4 className="font-bold text-lg mb-4 border-b border-gray-300 pb-2">
                RECEIPTS
              </h4>

              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Opening Balance (from {previousDate || 'N/A'})</span>
                  <span>₹{leftSideData.openingBalance?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>BAR SALES</span>
                  <span>₹{leftSideData.barSales?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>KITCHEN SALES</span>
                  <span>₹{leftSideData.kitchenSales?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>BEVERAGE SALES</span>
                  <span>₹{leftSideData.beverageSales?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="border-b border-gray-300 my-2"></div>
                <div className="flex justify-between">
                  <span>SGST 2.5 %</span>
                  <span>₹{leftSideData.sgst?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>CGST 2.5 %</span>
                  <span>₹{leftSideData.cgst?.toFixed(2) || "0.00"}</span>
                </div>

                <div className="flex justify-between font-bold border-t border-gray-300 pt-2 mt-2">
                  <span>TOTAL</span>
                  <span>₹{leftSideData.grandTotal?.toFixed(2) || "0.00"}</span>
                </div>
              </div>

              {Object.keys(leftSideData.paymentBreakdown || {}).length > 0 && (
                <div className="mt-6">
                  <h4 className="font-bold text-lg mb-4 border-b border-gray-300 pb-2">
                    PAYMENT METHODS
                  </h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(leftSideData.paymentBreakdown || {}).map(
                      ([method, amount]) => (
                        <div key={method} className="flex justify-between">
                          <span>{method.toUpperCase()}</span>
                          <span>₹{Number(amount).toFixed(2)}</span>
                        </div>
                      )
                    )}
                    <div className="flex justify-between font-bold border-t border-gray-300 pt-2 mt-2">
                      <span>Total</span>
                      <span>₹{leftSideData.totalSales?.toFixed(2) || "0.00"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-lg border-b border-gray-300 pb-2">
                  CUSTOM SECTIONS
                </h4>
                {!isEditable && <span className="text-sm text-gray-500">Report is saved and not editable</span>}
                <div className="flex gap-2">
                  <button
                    onClick={addNewSection}
                    disabled={!isEditable}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-3 py-1 rounded text-sm">
                    Add Section
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {rightSideSections.map((section, sectionIndex) => (
                  <div
                    key={sectionIndex}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={section.heading}
                        onChange={(e) =>
                          updateSectionHeading(sectionIndex, e.target.value)
                        }
                        disabled={!isEditable}
                        className={`flex-1 font-bold text-sm border border-gray-300 rounded px-2 py-1 bg-white ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder="Section name"
                      />
                      <button
                        onClick={() => addEntryToSection(sectionIndex)}
                        disabled={!isEditable}
                        className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-2 py-1 rounded text-xs">
                        + Entry
                      </button>
                      <button
                        onClick={() => removeSection(sectionIndex)}
                        disabled={!isEditable || rightSideSections.length === 1}
                        className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-2 py-1 rounded text-xs">
                        ✕ Section
                      </button>
                    </div>

                    <div className="space-y-1">
                      {section.entries.map((entry, entryIndex) => (
                        <div
                          key={entryIndex}
                          className="flex items-center gap-2">
                          <input
                            type="text"
                            value={entry.name}
                            onChange={(e) =>
                              updateEntry(
                                sectionIndex,
                                entryIndex,
                                "name",
                                e.target.value
                              )
                            }
                            disabled={!isEditable}
                            placeholder="Entry name"
                            className={`flex-1 border border-gray-300 rounded px-2 py-1 text-sm ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={entry.value}
                            onChange={(e) =>
                              updateEntry(
                                sectionIndex,
                                entryIndex,
                                "value",
                                e.target.value
                              )
                            }
                            disabled={!isEditable}
                            placeholder="0.00"
                            className={`w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          />
                          <button
                            onClick={() =>
                              removeEntry(sectionIndex, entryIndex)
                            }
                            disabled={!isEditable || section.entries.length === 1}
                            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-2 py-1 rounded text-xs">
                            ✕
                          </button>
                        </div>
                      ))}

                      <div className="flex justify-between font-semibold border-t border-gray-300 pt-1 mt-2 text-sm">
                        <span>{section.heading} Total</span>
                        <span>
                          ₹{section.entries
                            .reduce(
                              (sum, entry) => sum + (Number(entry.value) || 0),
                              0
                            )
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex justify-between font-bold border-t-2 border-gray-800 pt-2 mt-4 text-lg bg-gray-100 px-3 py-2 rounded">
                  <span>GRAND TOTAL</span>
                  <span>₹{rightSideTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t-2 border-gray-800">
            <div className="flex justify-between font-bold text-lg">
              <span>CLOSING BALANCE</span>
              <span>₹{finalClosingBalance.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Stock Preview Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">Daily Stock Report Preview</h3>
            <button
              onClick={() => setShowStockPreview(!showStockPreview)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors">
              {showStockPreview ? "Hide Stock Preview" : "Show Stock Preview"}
            </button>
          </div>

          {showStockPreview && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th rowspan="2" className="border border-gray-300 px-0.5 py-2 text-left font-bold" style={{ minWidth: '45px', maxWidth: '60px' }}>ITEM</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">OPENING</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">RECEIVED</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">TOTAL</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">TRANSFER</th>
                    <th colSpan="4" className="border border-gray-300 px-2 py-2 text-center font-bold">SALES</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">RATE</th>
                    <th colSpan="3" className="border border-gray-300 px-2 py-2 text-center font-bold">CLOSING</th>
                    <th rowspan="2" className="border border-gray-300 px-3 py-2 text-right font-bold">AMOUNT (₹)</th>
                  </tr>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">ML</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">ML</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">ML</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">ML</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">60ml</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">30ml</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">60ml</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">30ml</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">BTL</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">ML</th>
                    <th className="border border-gray-300 px-2 py-1 text-center font-bold">PC</th>
                  </tr>
                </thead>
                <tbody>
                  {buildStockReportData(selectedDate, stocks).categories.map((cat, catIdx) => (
                    <React.Fragment key={catIdx}>
                      <tr className="bg-gray-200">
                        <td colSpan="23" className="border border-gray-300 px-3 py-2 font-bold text-left text-gray-800">
                          {cat.category.toUpperCase()}
                        </td>
                      </tr>
                      {cat.items.map((item, itemIdx) => {
                        const totalBottleSold = Object.keys(item.sales || {})
                          .filter((k) => !["30 ml", "60 ml"].includes(k.toLowerCase()))
                          .reduce((sum, k) => sum + safeNum(item.sales[k]), 0);
                        const rate60ml = safeNum(item.unitRates?.["60 ml"] || 0);
                        const rate30ml = safeNum(item.unitRates?.["30 ml"] || 0);
                        const rateBottle = Object.keys(item.unitRates || {})
                          .filter((k) => !["30 ml", "60 ml"].includes(k.toLowerCase()))
                          .reduce((rate, k) => rate || safeNum(item.unitRates[k]), 0);

                        return (
                          <tr key={itemIdx} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-0.5 py-1 text-left font-medium" style={{ minWidth: '45px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(item.stockName || "").substring(0, 12)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.openingBTL.bottles}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : Math.round(item.openingBTL.remainder)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.openingPC || "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.receivedBTL.bottles}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : Math.round(item.receivedBTL.remainder)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.receivedPC || "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.totalBTL.bottles}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : Math.round(item.totalBTL.remainder)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.totalPC || "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.transferredBTL.bottles}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : Math.round(item.transferredBTL.remainder)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.transferredPC || "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{safeNum(item.sales["60 ml"] || 0)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{safeNum(item.sales["30 ml"] || 0)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : totalBottleSold}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? totalBottleSold : "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{rate60ml > 0 ? rate60ml.toFixed(0) : "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{rate30ml > 0 ? rate30ml.toFixed(0) : "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{rateBottle > 0 ? rateBottle.toFixed(0) : "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.closingBTL.bottles}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.isPiece ? "-" : Math.round(item.closingBTL.remainder)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-center">{item.closingPC || "-"}</td>
                            <td className="border border-gray-300 px-2 py-1 text-right font-medium">{safeNum(item.amount).toFixed(0)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-150 font-semibold">
                        <td className="border border-gray-300 px-3 py-1 text-right">SUBTOTAL ({cat.category})</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">-</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{safeNum(cat.totals.amount).toFixed(0)}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="bg-gray-300 font-bold">
                    <td className="border border-gray-300 px-3 py-2 text-right">GRAND TOTAL</td>
                    <td colSpan="21" className="border border-gray-300 px-2 py-2 text-center">-</td>
                    <td className="border border-gray-300 px-2 py-2 text-right">{buildStockReportData(selectedDate, stocks).grandTotalAmount.toFixed(0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <button
            onClick={handleSaveReport}
            disabled={reportSaved || hasPendingOrders}
            className={`px-6 py-3 rounded-lg font-semibold ${reportSaved || hasPendingOrders
              ? "bg-gray-400 text-gray-700 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}>
            {reportSaved ? "✓ Report Saved" : hasPendingOrders ? "Save Disabled - Pending Orders" : "Save Report"}
          </button>

          <button
            onClick={handleDownloadReport}
            disabled={!reportSaved}
            className={`px-6 py-3 rounded-lg font-semibold ${!reportSaved
              ? "bg-gray-400 text-gray-700 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white"
              }`}>
            Download/Print Report (2 Pages)
          </button>
        </div>

        {!reportSaved && (
          <p className="text-center text-gray-600 mt-4 text-sm">
            * You must save the report before downloading
          </p>
        )}
      </div>
    </div>
  );
};

export default EndDay;